import {
  ApplicationInsights,
  SeverityLevel,
  ITelemetryItem
} from '@microsoft/applicationinsights-web'

type AppInsightsConfig = {
  enabled?: boolean | string
  connectionString?: string
  samplingPercentage?: number | string
}

// Matches the platform's "{ServiceName}-{Environment}" CloudRoleName convention.
const SERVICE_NAME = 'cloudadmin'

let _ai: ApplicationInsights | null = null

// The deployed appsettings.json is generated by the release pipeline, which
// stringifies every value — "false" must not read as enabled.
const isOff = (value: unknown) => value === false || value === 'false'

// Query strings can carry secrets (blob SAS signatures, invitation tokens) and
// personal data (emails), so no URL is sent with one.
const stripQuery = (url: unknown) =>
  typeof url === 'string' ? url.split('?')[0] : url

const URL_FIELDS = ['uri', 'refUri', 'target', 'url']

type TelemetryHostConfig = {
  environment?: string
  instanceName?: string
  buildBranchNumber?: string
  metadataUrl?: string
  appInsights?: AppInsightsConfig
}

export function initAi(config: TelemetryHostConfig) {
  const settings = config?.appInsights || {}

  if (isOff(settings.enabled) || !settings.connectionString) return

  // instanceName tells apart instances that share an environment (dev0 runs
  // as "dev"); environment stays the scope the rest of the app gates on.
  const environment = config?.instanceName || config?.environment || 'unknown'
  const buildBranchNumber = config?.buildBranchNumber || ''
  const cloudRole = `${SERVICE_NAME}-${environment}`

  // Only our own API host (every service client is built on metadataUrl) gets
  // correlation headers: a host that doesn't allow them in CORS (e.g. the
  // login servers) would fail its preflight.
  const correlationHeaderDomains = config?.metadataUrl
    ? [new URL(config.metadataUrl).host]
    : []

  const ai = new ApplicationInsights({
    config: {
      connectionString: settings.connectionString,
      enableAutoRouteTracking: true,
      enableUnhandledPromiseRejectionTracking: true,
      enableCorsCorrelation: true,
      correlationHeaderDomains,
      samplingPercentage: Number(settings.samplingPercentage ?? 100) || 100,
      autoTrackPageVisitTime: true
    }
  })

  try {
    ai.loadAppInsights()
  } catch (error) {
    // A malformed connection string must not break bootstrap or leave a
    // half-started SDK behind for the facade to call into.
    // eslint-disable-next-line no-console
    console.error('Application Insights failed to start', error)
    return
  }

  ai.addTelemetryInitializer((item: ITelemetryItem) => {
    item.tags = item.tags || []
    item.tags['ai.cloud.role'] = cloudRole
    item.data = item.data || {}
    item.data.environment = environment
    if (buildBranchNumber) item.data.build = buildBranchNumber

    const base = item.baseData
    if (base) {
      URL_FIELDS.forEach(field => {
        if (field in base) base[field] = stripQuery(base[field])
      })
      // A dependency's name is "<METHOD> <url>".
      if (item.baseType === 'RemoteDependencyData') {
        base.name = stripQuery(base.name)
      }
    }
  })

  _ai = ai
}

export function setAuthenticatedUser(userId?: string, tenantId?: string) {
  if (!_ai || !userId) return
  _ai.setAuthenticatedUserContext(userId, tenantId, true)
}

export function clearAuthenticatedUser() {
  if (!_ai) return
  _ai.clearAuthenticatedUserContext()
}

export function trackException(
  error: unknown,
  properties?: Record<string, any>,
  severityLevel: SeverityLevel = SeverityLevel.Error
) {
  if (!_ai) return
  const exception = error instanceof Error ? error : new Error(String(error))
  _ai.trackException({ exception, severityLevel }, properties)
}

export function trackApiException(
  resp: { url?: string; status?: number },
  error: Error
) {
  trackException(error, {
    url: stripQuery(resp?.url),
    httpStatus: resp?.status
  })
}

export function flushAi() {
  if (!_ai) return
  _ai.flush()
}

// Shared by every way a session ends (logout here, logout in another tab,
// session lost) so nothing after it is attributed to the user who left.
export function endAuthenticatedSession() {
  flushAi()
  clearAuthenticatedUser()
}

// Test seam: the SDK keeps module-level state, so suites that init must be
// able to put it back.
export function resetAiForTests() {
  _ai = null
}
